import { useSession } from '@emerald-works/react-auth'
import { CircularProgress } from '@mui/material'
import {
  CheckSquare,
  Eye,
  EyeSlash,
  ListPlus,
  MonitorPlay,
  PlayCircle,
  Plus,
  ThumbsUp,
  Trash
} from '@phosphor-icons/react'
import React, { useCallback, useMemo } from 'react'
import { useGroup } from '../../hooks'
import useFreeAccountCta from '../../hooks/useFreeAccountCta'
import { UserGroupPolicy } from '../user-group-policy'
import { ALLOW_BOOKMARKS_GROUP, ALLOW_PLAYLISTS_GROUP } from '../user-group-policy/permissions'
import ActionButton from './action-button'
import MoreActions from './more-actions'

// MT.3 brand refresh — action buttons render Phosphor icons. Weight mirrors
// the MUI variant intent: `duotone` = TwoTone, `fill` = filled, `regular`
// = outlined/border. A fixed pixel size (rather than `1em`) keeps icons
// visually consistent across `<Button>` and `<IconButton>` wrappers:
// MUI's `startIcon` slot has its own internal font-size that would
// otherwise make icons inside text buttons render larger than icons
// inside icon-only (hideText) buttons.
export const ICON_SIZE = 18

export const StartPlaylistButton = ({ ...props }) => {
  return (
    <UserGroupPolicy
      accessGroup={ALLOW_PLAYLISTS_GROUP}
      componentAllow={
        <ActionButton
          variant='primary'
          startIcon={<PlayCircle size={ICON_SIZE} weight='duotone' />}
          label='Open'
          {...props}
        />
      }
    />
  )
}

export const AddPlaylistToLibraryButton = ({ ...props }) => {
  return (
    <UserGroupPolicy
      accessGroup={ALLOW_PLAYLISTS_GROUP}
      componentAllow={
        <ActionButton
          variant='secondary'
          startIcon={<Plus size={ICON_SIZE} weight='regular' />}
          label='Add to Library'
          {...props}
        />
      }
    />
  )
}

export const RemoveFromPlaylistButton = ({ ...props }) => {
  return (
    <UserGroupPolicy
      accessGroup={ALLOW_PLAYLISTS_GROUP}
      componentAllow={
        <ActionButton
          variant='secondary'
          startIcon={<Trash size={ICON_SIZE} weight='duotone' />}
          label='Remove'
          {...props}
        />
      }
    />
  )
}

export const ResumePlaylistButton = ({ ...props }) => {
  return (
    <UserGroupPolicy
      accessGroup={ALLOW_PLAYLISTS_GROUP}
      componentAllow={
        <ActionButton
          variant='grey'
          startIcon={<PlayCircle size={ICON_SIZE} weight='duotone' />}
          label='Resume'
          {...props}
        />
      }
    />
  )
}

export const RevisitPlaylistButton = ({ ...props }) => {
  return (
    <UserGroupPolicy
      accessGroup={ALLOW_PLAYLISTS_GROUP}
      componentAllow={
        <ActionButton
          variant='grey'
          startIcon={<MonitorPlay size={ICON_SIZE} weight='duotone' />}
          label='Revisit'
          {...props}
        />
      }
    />
  )
}

export const RemoveFromLibraryButton = ({ ...props }) => {
  return <ActionButton variant='ghost' label='Remove from Library' {...props} />
}

export const LikeButton = ({ likes, hasLiked, onClick, ...props }) => {
  const { isAuthenticated } = useSession()
  const { openFreeAccountModalForLike } = useFreeAccountCta()

  const handleClick = useCallback(() => {
    if (!isAuthenticated) {
      return openFreeAccountModalForLike()
    }

    if (onClick) onClick()
  }, [onClick, openFreeAccountModalForLike, isAuthenticated])

  return (
    <ActionButton
      variant={hasLiked ? 'secondary' : 'secondary-noselect'}
      startIcon={
        <ThumbsUp
          size={ICON_SIZE}
          weight={hasLiked ? 'fill' : 'duotone'}
        />
      }
      label={likes ? String(likes) : undefined}
      onClick={handleClick}
      {...props}
    />
  )
}
export const BookmarkButton = ({ bookmarked, onClick, ...props }) => {
  const { isInGroup } = useGroup()
  const canBookmark = useMemo(() => isInGroup(ALLOW_BOOKMARKS_GROUP), [isInGroup])
  const { isAuthenticated } = useSession()
  const { openFreeAccountModalForAddContentToBookmark } = useFreeAccountCta()

  const handleClick = useCallback(() => {
    if (!isAuthenticated) {
      return openFreeAccountModalForAddContentToBookmark()
    }

    if (onClick) onClick()
  }, [onClick, openFreeAccountModalForAddContentToBookmark, isAuthenticated])

  // Hide if user isn't on bookmark group and already have an account
  if (!canBookmark && isAuthenticated) return null

  return (
    <ActionButton
      variant='none'
      label={bookmarked ? 'Bookmarked' : 'Bookmark'}
      {...props}
      onClick={handleClick}
    />
  )
}

export const AddToPlaylistButton = ({ ...props }) => {
  return (
    <UserGroupPolicy
      accessGroup={ALLOW_PLAYLISTS_GROUP}
      componentAllow={
        <ActionButton
          variant='none'
          label='Add to Playlist'
          startIcon={<ListPlus size={ICON_SIZE} weight='duotone' />}
          {...props}
        />
      }
    />
  )
}

export const ToggleDescriptionButton = ({ toggled, ...props }) => {
  return (
    <ActionButton
      variant='secondary'
      label={!toggled ? 'See Description' : 'Close Description'}
      startIcon={
        !toggled
          ? <Eye size={ICON_SIZE} weight='duotone' />
          : <EyeSlash size={ICON_SIZE} weight='duotone' />
      }
      {...props}
    />
  )
}

export const ToggleViewedButton = ({ viewed, onClick, loadingIndicator, ...props }) => {
  const { isInGroup } = useGroup()
  const canAccessPlaylist = useMemo(() => isInGroup(ALLOW_PLAYLISTS_GROUP), [isInGroup])
  const { isAuthenticated } = useSession()

  if (!canAccessPlaylist && isAuthenticated) return null

  return (
    <ActionButton
      variant={viewed ? 'contained' : 'outlined'}
      color={viewed ? 'success' : 'secondaryGrey'}
      label={viewed ? 'Viewed' : 'Mark as viewed'}
      {...props}
      endIcon={loadingIndicator && <CircularProgress size={16} />}
      onClick={onClick}
      disabled={viewed}
      startIcon={viewed ? <CheckSquare size={ICON_SIZE} weight='fill' /> : null}
    />
  )
}

export { ActionButton, MoreActions }
